import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { ShoppingCart, ArrowLeft, Package, Shield, Truck, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetails = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [reviewsData, setReviewsData] = useState({ reviews: [], average_rating: 0, total_reviews: 0 });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/product/${productId}`);
      setReviewsData(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      toast.success('Review submitted successfully. It will be visible after approval.');
      setReviewForm({ rating: 5, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      // Fetch related products from same category
      try {
        const relatedRes = await axios.get(`${API}/products`, {
          params: { category: response.data.category }
        });
        setRelatedProducts(
          relatedRes.data.filter(p => p.id !== productId).slice(0, 4)
        );
      } catch (err) {
        console.error('Failed to fetch related products:', err);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    toast.success(`Added ${quantity} item(s) to cart!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product Not Found</h2>
          <Link to="/products">
            <Button className="bg-primary hover:bg-primary/90">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="product-details-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-primary transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Products
          </Link>
        </div>

        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Image */}
          <div className="aspect-square overflow-hidden bg-slate-100 rounded-2xl border border-slate-200 shadow-lg">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                data-testid="product-image"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300">
                <Package className="h-32 w-32" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col justify-center">
            <p className="text-sm uppercase tracking-widest text-primary font-semibold mb-2">
              {product.category}
            </p>

            <h1
              className="text-3xl md:text-4xl font-heading font-bold tracking-tight uppercase mb-4"
              data-testid="product-detail-name"
            >
              {product.name}
            </h1>

            {reviewsData.total_reviews > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`h-5 w-5 ${s <= Math.round(reviewsData.average_rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                  ))}
                </div>
                <span className="text-sm font-semibold text-slate-700">{reviewsData.average_rating}</span>
                <span className="text-sm border-l pl-2 text-slate-500">({reviewsData.total_reviews} reviews)</span>
              </div>
            )}

            <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            <div className="flex items-baseline gap-4 mb-6">
              <span className="text-4xl font-bold text-primary" data-testid="product-detail-price">
                ₹{product.price?.toLocaleString()}
              </span>
              {product.warranty_months && (
                <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                  {product.warranty_months} months warranty
                </span>
              )}
            </div>

            <div className="flex items-center gap-4 mb-6">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                product.stock > 0
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
              </span>
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-sm font-semibold text-slate-700">Quantity:</span>
              <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="px-4 py-2 bg-slate-50 hover:bg-slate-100 transition-colors font-bold text-lg"
                  data-testid="quantity-decrease"
                >
                  −
                </button>
                <span className="px-6 py-2 font-semibold text-lg" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                  className="px-4 py-2 bg-slate-50 hover:bg-slate-100 transition-colors font-bold text-lg"
                  data-testid="quantity-increase"
                >
                  +
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider py-6 h-auto text-lg"
                data-testid="add-to-cart-detail"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add to Cart
              </Button>
              <Button
                onClick={() => {
                  handleAddToCart();
                  navigate('/cart');
                }}
                disabled={product.stock === 0}
                variant="outline"
                className="flex-1 border-primary text-primary hover:bg-primary/5 font-bold uppercase tracking-wider py-6 h-auto text-lg"
                data-testid="buy-now-detail"
              >
                Buy Now
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col items-center text-center p-3 bg-slate-50 rounded-xl">
                <Shield className="h-6 w-6 text-primary mb-2" />
                <span className="text-xs font-medium text-slate-600">Warranty</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-slate-50 rounded-xl">
                <Truck className="h-6 w-6 text-primary mb-2" />
                <span className="text-xs font-medium text-slate-600">Free Delivery</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-slate-50 rounded-xl">
                <Star className="h-6 w-6 text-primary mb-2" />
                <span className="text-xs font-medium text-slate-600">Premium Quality</span>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications */}
        {product.specifications && Object.keys(product.specifications).length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-heading font-bold uppercase mb-6">
              <span className="text-primary">Specifications</span>
            </h2>
            <Card className="border-slate-200 overflow-hidden">
              <div className="divide-y divide-slate-100">
                {Object.entries(product.specifications).map(([key, value], index) => (
                  <div
                    key={key}
                    className={`flex py-4 px-6 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                    data-testid={`spec-${key}`}
                  >
                    <span className="font-semibold text-slate-700 w-1/3 capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-slate-600 w-2/3">{value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-heading font-bold uppercase mb-6">
              RELATED <span className="text-primary">PRODUCTS</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(rp => (
                <Link key={rp.id} to={`/products/${rp.slug || rp.id}`} className="group">
                  <Card className="overflow-hidden border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="aspect-square bg-slate-100 overflow-hidden">
                      {rp.image_url ? (
                        <img
                          src={rp.image_url}
                          alt={rp.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <Package className="h-12 w-12" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{rp.category}</p>
                      <h3 className="font-semibold text-sm mb-2 line-clamp-2">{rp.name}</h3>
                      <span className="text-primary font-bold">₹{rp.price?.toLocaleString()}</span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Customer Reviews Section */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-heading font-bold uppercase mb-6">
            CUSTOMER <span className="text-primary">REVIEWS</span>
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="col-span-1 lg:col-span-1 order-2 lg:order-1">
              {/* Review Form */}
              <Card className="p-6 border-slate-200 shadow-sm sticky top-24">
                <h3 className="font-heading font-bold text-lg mb-4">Write a Review</h3>
                {!user ? (
                  <div className="bg-slate-50 p-4 rounded-lg text-center border border-slate-100">
                    <p className="text-sm text-slate-600 mb-4">You need to be logged in to write a review.</p>
                    <Button onClick={() => navigate('/login')} className="w-full bg-primary hover:bg-primary/90">
                      Login to Review
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitReview} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                            className="focus:outline-none transition-transform hover:scale-110"
                          >
                            <Star className={`h-8 w-8 ${star <= reviewForm.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200 hover:text-amber-200'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Review</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm h-32 resize-none"
                        placeholder="Share your experience with this product..."
                        required
                        minLength={10}
                      ></textarea>
                    </div>
                    <Button type="submit" disabled={submittingReview} className="w-full bg-slate-900 hover:bg-slate-800 tracking-wide uppercase font-semibold">
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </form>
                )}
              </Card>
            </div>

            <div className="col-span-1 lg:col-span-2 order-1 lg:order-2">
              <div className="space-y-6">
                {reviewsData.reviews.length === 0 ? (
                  <div className="py-12 text-center rounded-xl bg-slate-50 border border-slate-100">
                    <Star className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-1">No reviews yet</h3>
                    <p className="text-slate-500">Be the first to review this product!</p>
                  </div>
                ) : (
                  reviewsData.reviews.map(review => (
                    <Card key={review.id} className="p-6 border-slate-200 hover:border-slate-300 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/30 flex items-center justify-center text-primary font-bold text-lg">
                            {review.user_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{review.user_name}</p>
                            <p className="text-xs text-slate-500">{new Date(review.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} className={`h-4 w-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-700 leading-relaxed pl-16">
                        "{review.comment}"
                      </p>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
